/**
 * The space's Settings tab, pure parts: each client access setting's words, the two groups
 * the tab shows ("Shown to …" / "Withheld from …", by what is saved, so a
 * row does not jump while it is switched), the change a save sends, and the
 * organizations a space may be linked to.
 *
 * @module components/spaces/SpaceSettings.logic
 */
import { CLIENT_ACCESS_KEYS, type ClientAccess, type ClientAccessKey } from "../../state/spaces";

export interface ClientAccessSetting {
  readonly key: ClientAccessKey;
  readonly label: string;
  /** One line on what turning it on shows; `{client}` is the client's name. */
  readonly explanation: string;
}

export const CLIENT_ACCESS_SETTINGS: ReadonlyArray<ClientAccessSetting> = [
  { key: "policies", label: "Policies", explanation: "The current version of each policy." },
  { key: "procedures", label: "Procedures", explanation: "The current version of each procedure." },
  {
    key: "versions",
    label: "Versions and change history",
    explanation: "Earlier versions of the documents shown, and what changed.",
  },
  {
    key: "findings",
    label: "Findings and their resolution",
    explanation: "Findings on the documents shown, open and resolved.",
  },
  {
    key: "findingBasis",
    label: "Regulatory basis of findings",
    explanation: "The rule behind each finding: its title, regulator and jurisdiction.",
  },
  {
    key: "recordUpdates",
    label: "Suggestions",
    explanation: "Applied suggestions on the documents shown.",
  },
  {
    key: "regimes",
    label: "Regimes and regulators",
    explanation: "The regimes and regulators {client} follows.",
  },
  {
    key: "knowledge",
    label: "Knowledge given to the agent",
    explanation: "Reference documents the agent answers from.",
  },
];

/** The explanation with the client's name in it. */
export function settingExplanation(setting: ClientAccessSetting, client: string): string {
  return setting.explanation.replace(/\{client\}/g, client);
}

/** The settings split by what is saved: shown first, withheld second, each in the list's order. */
export function groupClientAccess(saved: ClientAccess): {
  readonly shown: ReadonlyArray<ClientAccessSetting>;
  readonly withheld: ReadonlyArray<ClientAccessSetting>;
} {
  return {
    shown: CLIENT_ACCESS_SETTINGS.filter((setting) => saved[setting.key]),
    withheld: CLIENT_ACCESS_SETTINGS.filter((setting) => !saved[setting.key]),
  };
}

/** The keys whose draft differs from what is saved: what a save sends. Empty when nothing changed. */
export function clientAccessChanges(saved: ClientAccess, draft: ClientAccess): Partial<ClientAccess> {
  const changes: Partial<Record<ClientAccessKey, boolean>> = {};
  for (const key of CLIENT_ACCESS_KEYS) {
    if (saved[key] !== draft[key]) changes[key] = draft[key];
  }
  return changes;
}

/**
 * The organizations a space may serve: every one the session sees except the
 * host itself (the server refuses that too), by name.
 */
export function linkableOrganizations<T extends { readonly id: string; readonly name: string }>(
  orgs: ReadonlyArray<T>,
  hostOrganizationId: string | null,
): T[] {
  return orgs
    .filter((org) => org.id !== hostOrganizationId)
    .toSorted((left, right) => left.name.localeCompare(right.name));
}

/** A description the server will take: trimmed, blank as none. */
export function normalizeDescriptionDraft(draft: string): string | null {
  const trimmed = draft.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** The server's limit (`SPACE_DESCRIPTION_MAX_LENGTH`), counted in code points as it counts them. */
export const SPACE_DESCRIPTION_MAX_LENGTH = 280;

export const descriptionLength = (draft: string): number => [...draft.trim()].length;
